import {
  CdkVirtualScrollViewport,
  CdkVirtualForOf,
  CdkFixedSizeVirtualScroll,
} from '@angular/cdk/scrolling';
import type { OnDestroy, OnInit, ViewRef } from '@angular/core';
import { ChangeDetectorRef, Component, forwardRef, ViewChild } from '@angular/core';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';

import type { KeyboardEvent } from 'react';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { KeyboardEventCode } from '@core/common-legacy/keyboard';
import type { MentionElement, MentionNavigationKey } from '@core/prosemirror-legacy';
import { MENTION_STRATEGY_TOKEN } from '@core/prosemirror-legacy';
import {
  TruncateComponent,
  AvatarComponent,
  DropdownItemComponent,
  IconComponent,
  InputComponent,
  FormFieldComponent,
  DropdownComponent,
} from '@core/user-interface-legacy';

import { MentionsListService } from './mentions-list.service';

@Component({
  selector: 'rt-mentions',
  templateUrl: './mentions.component.html',
  styleUrls: ['./mentions.component.less'],
  host: {
    '(keyup)': 'onKeyUp($event)',
    '(keydown)': 'onKeyDown($event)',
  },
  providers: [
    {
      provide: MENTION_STRATEGY_TOKEN,
      // eslint-disable-next-line @angular-eslint/no-forward-ref
      useClass: forwardRef(() => MentionsListService),
    },
  ],
  imports: [
    DropdownComponent,
    FormFieldComponent,
    InputComponent,
    ReactiveFormsModule,
    IconComponent,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    DropdownItemComponent,
    AvatarComponent,
    TruncateComponent,
  ],
})
export class MentionsComponent implements OnInit, OnDestroy {
  public highlightedIndex = 0;
  public searchFormControl = new UntypedFormControl('');

  public searchEnabled: boolean;
  public queryChange: (val: string) => void;
  public select: (val: MentionElement) => void;
  public close: () => void;
  public data: MentionElement[];

  @ViewChild('scrollViewport') private cdkVirtualScrollViewport: CdkVirtualScrollViewport;
  private ngOnDestroy$ = new Subject<void>();

  constructor(private cdRef: ChangeDetectorRef) {}

  public ngOnInit() {
    this.searchFormControl.valueChanges
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(value => this.queryChange(value));
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public onKeyUp(event: KeyboardEvent) {
    this.handleKey(<MentionNavigationKey>event.key);
  }

  public onKeyDown(event: KeyboardEvent) {
    if (KeyboardEventCode.Escape === event.key) {
      event.stopPropagation();
      this.close();
    }
  }

  public onElementHover(index: number) {
    this.highlightedIndex = index;
    this.cdRef.detectChanges();
  }

  public handleKey(key: MentionNavigationKey): void {
    if (KeyboardEventCode.Enter === key) {
      const mention = this.data[this.highlightedIndex];
      if (mention) {
        this.select(mention);
      } else {
        this.close();
      }
    } else if (KeyboardEventCode.ArrowDown === key) {
      this.highlightedIndex = (this.highlightedIndex + 1) % this.data.length;

      this.cdRef.detectChanges();
    } else if (KeyboardEventCode.ArrowUp === key) {
      this.highlightedIndex =
        this.highlightedIndex === 0
          ? this.data.length - 1
          : (this.highlightedIndex - 1) % this.data.length;
      this.cdRef.detectChanges();
    }
  }

  public trackByMention(index: number, item: MentionElement) {
    return `${item.type}${item.id}`;
  }

  public getContainerHeight(): string {
    const numberOfItems = (this.data || []).length;
    const itemHeight = 49;
    const visibleItems = 4.5;

    setTimeout(() => {
      this.cdkVirtualScrollViewport.checkViewportSize();
      if (!(<ViewRef>this.cdRef).destroyed) {
        this.cdRef.detectChanges();
      }
    }, 300);

    if (numberOfItems <= visibleItems) {
      return `${itemHeight * numberOfItems}px`;
    }
    return `${itemHeight * visibleItems}px`;
  }
  public onScrolledIndexChange() {
    this.cdRef.detectChanges();
  }
}
